import { daysInMonth, countWorkedDays, dayPrintValue, OffCrewRow, offCrewTypeLabels } from './timesheetUtils';
import type { TimesheetRow } from './TimesheetGrid';

interface PrintArgs {
  vesselName: string;
  month: string; // yyyy-MM
  employees: any[];
  rows: Record<string, TimesheetRow>;
  offCrewRows: OffCrewRow[];
}

const monthLabel = (month: string): string => {
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};

/** Print view following the "Monthly Time Sheet" Excel layout:
 *  header, 1/0.5/0 legend, Crew + Off Crew Personnel sections, signatures. */
export function printTimesheet({ vesselName, month, employees, rows, offCrewRows }: PrintArgs): void {
  const totalDays = daysInMonth(month);
  const dayNumbers = Array.from({ length: totalDays }, (_, i) => i + 1);

  const dayCells = (days: Record<string, any>) =>
    dayNumbers.map((d) => `<td class="day">${dayPrintValue(days[String(d)])}</td>`).join('');

  const crewRows = employees.map((emp: any, idx: number) => {
    const key = emp.documentId || String(emp.id);
    const row = rows[key] || { days: {}, description: '' };
    return `<tr>
      <td>${idx + 1}</td><td>${emp.rank || '-'}</td><td class="name">${emp.name || '-'}</td>
      ${dayCells(row.days)}<td class="total">${countWorkedDays(row.days)}</td>
    </tr>`;
  }).join('');

  const offRows = offCrewRows.map((row, idx) => `<tr>
      <td>${idx + 1}</td><td>${offCrewTypeLabels[row.type]}</td>
      <td class="name">${row.name || '-'}${row.description ? ` — ${row.description}` : ''}</td>
      ${dayCells(row.days)}<td class="total">${countWorkedDays(row.days)}</td>
    </tr>`).join('');

  const sectionRow = (title: string) =>
    `<tr class="section"><td colspan="${totalDays + 4}">${title}</td></tr>`;

  const signatures = ['Master / CH.OFF', 'CH.ENG', 'Port Captain', 'Port Engineer'];

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Time Sheet - ${vesselName} - ${monthLabel(month)}</title>
  <style>
    body { font-family: Arial, sans-serif; font-size: 10px; margin: 16px; }
    h1 { text-align: center; font-size: 18px; margin: 0 0 8px; }
    .meta { display: flex; justify-content: space-between; margin-bottom: 6px; font-size: 12px; }
    .legend { margin-bottom: 8px; font-size: 10px; }
    .legend span { margin-left: 14px; }
    table { border-collapse: collapse; width: 100%; }
    th, td { border: 1px solid #333; padding: 2px 3px; text-align: center; }
    td.name { text-align: left; min-width: 120px; }
    td.day { width: 18px; }
    td.total { font-weight: bold; }
    tr.section td { background: #d9d9d9; font-weight: bold; text-align: left; }
    thead th { background: #f0f0f0; }
    .signatures { margin-top: 18px; }
    .signatures td { height: 22px; }
    @media print { body { margin: 6px; } }
  </style></head><body>
  <h1>Time Sheet</h1>
  <div class="meta">
    <div><strong>Month:</strong> ${monthLabel(month)}</div>
    <div><strong>On-Hire / Off-Hire Dates:</strong> ____________</div>
    <div><strong>Vessel:</strong> ${vesselName}</div>
  </div>
  <div class="legend">
    <span><strong>1</strong> = Working Day</span>
    <span><strong>0.5</strong> = تعيين / رفت (Sign On / Sign Off)</span>
    <span><strong>0</strong> = Non-Working Day</span>
  </div>
  <table>
    <thead><tr><th>#</th><th>Rank</th><th>Name</th>${dayNumbers.map((d) => `<th>${d}</th>`).join('')}<th>Working Days</th></tr></thead>
    <tbody>
      ${sectionRow('Crew')}
      ${crewRows || `<tr><td colspan="${totalDays + 4}">No crew</td></tr>`}
      ${sectionRow('Off Crew Personnel')}
      ${offRows || `<tr><td colspan="${totalDays + 4}">-</td></tr>`}
    </tbody>
  </table>
  <table class="signatures">
    <thead><tr><th>Signatures</th>${signatures.map((s) => `<th>${s}</th>`).join('')}</tr></thead>
    <tbody>
      <tr><td><strong>Name</strong></td>${signatures.map(() => '<td></td>').join('')}</tr>
      <tr><td><strong>Signature</strong></td>${signatures.map(() => '<td></td>').join('')}</tr>
      <tr><td><strong>Date</strong></td>${signatures.map(() => '<td></td>').join('')}</tr>
    </tbody>
  </table>
  </body></html>`;

  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 300);
}
